"use client";

/**
 * useRoadmapAnimation — drives the active step on the roadmap timeline from
 * the user's scroll position instead of a wall-clock interval.
 *
 * Algorithm
 *   - We measure the roadmap section's bounding rect each animation frame
 *     while it's intersecting the viewport.
 *   - We map (sectionTopRelativeToViewport) onto a 0..1 progress, biasing
 *     the active range so the user has to scroll through the section to
 *     light up subsequent steps (rather than all lighting at once when the
 *     top hits the viewport edge).
 *   - The active step is `floor(progress * stepCount)`, clamped.
 *
 * Why rAF rather than `onscroll`? scroll fires too often on some browsers
 * (Chrome/desktop), causing layout thrash. rAF naturally coalesces.
 */

import { useEffect, useState } from "react";
import { ROADMAP_STEPS } from "@/constants/home.constants";

export function useRoadmapAnimation() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const el = document.getElementById("roadmap-section");
    if (!el) return;

    let rafId = 0;
    let isVisible = false;

    const tick = () => {
      rafId = 0;
      const rect = el.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;

      // How far the user has scrolled past the section top, normalised to
      const total = rect.height + viewport;
      const scrolled = viewport - rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / total));

      // Bias the progress so the first step lights up almost immediately,
      // and the last lights up near the end. Multiplying by stepCount-1
      const idx = Math.round(progress * (ROADMAP_STEPS.length - 1));
      setActiveStep(idx);
    };

    const onScroll = () => {
      if (!isVisible || rafId) return;
      rafId = window.requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisible = entry.isIntersecting;
        if (isVisible) tick(); // sync once on entry so the initial render isn't stuck on step 0
      },
      { threshold: 0 },
    );

    observer.observe(el);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return { activeStep };
}
